import { useRef, useState, useEffect } from "react";
import "./MusicPlayer.css";
import { AiOutlinePause, AiOutlinePlayCircle } from "react-icons/ai";

export default function MusicPlayer() {
  const audioRef = useRef(null);
  const [isPlaying, setIsPlaying] = useState(false);
  const [errorMsg, setErrorMsg] = useState("");
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);
  const [isSeeking, setIsSeeking] = useState(false);
  const [volume, setVolume] = useState(1);

  // Update current time while audio is playing
  useEffect(() => {
    const audio = audioRef.current;
    if (!audio) return;

    const updateTime = () => {
      if (!isSeeking) {
        setCurrentTime(audio.currentTime);
      }
    };

    const updateDuration = () => {
      setDuration(audio.duration || 0);
    };

    audio.addEventListener("timeupdate", updateTime);
    audio.addEventListener("loadedmetadata", updateDuration);
    audio.addEventListener("durationchange", updateDuration);

    return () => {
      audio.removeEventListener("timeupdate", updateTime);
      audio.removeEventListener("loadedmetadata", updateDuration);
      audio.removeEventListener("durationchange", updateDuration);
    };
  }, [isSeeking]);

  const togglePlay = () => {
    if (!audioRef.current) return;

    if (isPlaying) {
      audioRef.current.pause();
      setIsPlaying(false);
    } else {
      audioRef.current
        .play()
        .then(() => {
          setErrorMsg(""); // clear any old error
          setIsPlaying(true);
        })
        .catch((err) => {
          console.error("Playback error:", err);
          setErrorMsg(err.message || "Unknown playback error");
        });
    }
  };

  const handleSeek = (e) => {
    if (!audioRef.current) return;
    const newTime = parseFloat(e.target.value);
    setCurrentTime(newTime);
    audioRef.current.currentTime = newTime;
  };

  const handleSeekStart = () => {
    setIsSeeking(true);
  };

  const handleSeekEnd = () => {
    setIsSeeking(false);
  };

  const handleVolumeChange = (e) => {
    const newVolume = parseFloat(e.target.value);
    setVolume(newVolume);
    if (audioRef.current) {
      audioRef.current.volume = newVolume;
    }
  };

  const formatTime = (seconds) => {
    if (!isFinite(seconds) || seconds < 0) return "0:00";

    const mins = Math.floor(seconds / 60);
    const secs = Math.floor(seconds % 60);
    return `${mins}:${secs.toString().padStart(2, "0")}`;
  };

  const progressPercentage = duration > 0 ? (currentTime / duration) * 100 : 0;

  return (
    <div className="container">
      <div className="music-player">
        <div className="music-player__content">
          {/* Album cover */}
          <img
            src="./153514624_117642200305302_1988568569907846343_n.jpg"
            alt="Frank Ocean - Blonde"
            className="music-player__album"
          />

          {/* Info */}
          <div className="music-player__info">
            <h3 className="music-player__title">Pink + White</h3>
            <p className="music-player__artist">Frank Ocean</p>

            {/* Progress Bar */}
            <div className="music-player__progress">
              <div className="music-player__time">
                <span>{formatTime(currentTime)}</span>
                <span>{formatTime(duration)}</span>
              </div>

              <div className="music-player__progress-bar">
                <div
                  className="music-player__progress-fill"
                  style={{ width: `${progressPercentage}%` }}
                />
                <input
                  type="range"
                  min="0"
                  max={duration || 100}
                  value={currentTime}
                  onChange={handleSeek}
                  onMouseDown={handleSeekStart}
                  onMouseUp={handleSeekEnd}
                  onTouchStart={handleSeekStart}
                  onTouchEnd={handleSeekEnd}
                  className="music-player__progress-input"
                  disabled={!duration}
                />
              </div>
            </div>

            {/* Controls */}
            <div className="music-player__controls">
              <button
                onClick={togglePlay}
                className="music-player__play-button"
                aria-label={isPlaying ? "Pause" : "Play"}
              >
                {isPlaying ? <AiOutlinePause /> : <AiOutlinePlayCircle />}
              </button>

              {/* Volume control */}
              {/* <div className="music-player__volume">
                <span className="music-player__volume-icon">
                  {volume === 0 ? "🔇" : volume < 0.5 ? "🔈" : "🔊"}
                </span>
                <input
                  type="range"
                  min="0"
                  max="1"
                  step="0.01"
                  value={volume}
                  onChange={handleVolumeChange}
                  className="music-player__volume-slider"
                  aria-label="Volume control"
                />
              </div> */}
            </div>

            {/* Error Notification */}
            {errorMsg && (
              <div className="music-player__error">
                ⚠️ Playback failed: {errorMsg}
              </div>
            )}
          </div>
        </div>

        {/* Hidden Audio */}
        <audio
          ref={audioRef}
          src="https://files.catbox.moe/8263f8.flac"
          onError={(e) =>
            setErrorMsg(
              e?.target?.error?.message || "Media file could not be loaded."
            )
          }
          onEnded={() => setIsPlaying(false)}
        />
      </div>
    </div>
  );
}
